/**
 * GenericDataModal - Display structured data in a modal
 *
 * A reusable modal component that displays structured data fields
 * with copy functionality and status indicators.
 *
 * Features:
 * - Read-only field display with labels
 * - Copy button for each field
 * - Status indicators (green check for filled, red alert for missing)
 * - Completeness indicator
 * - Responsive design (mobile bottom sheet, desktop centered)
 * - "Copy All" action button
 */

import React, { useState, useCallback } from 'react'
import type {
  StructuredData,
  StructuredDataField,
} from '../../types/structuredData'
import { DynamicIcon, Icon } from '@/design-system/atoms/Icon'
import { calculateCompleteness } from '../../types/structuredData'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'

interface GenericDataModalProps {
  data: StructuredData
  isOpen: boolean
  onClose: () => void
}

interface FieldItemProps {
  field: StructuredDataField
  onCopy: (value: string) => void
  copiedField: string | null
}

const FieldItem: React.FC<FieldItemProps> = ({
  field,
  onCopy,
  copiedField,
}) => {
  const hasValue =
    field.value !== null && field.value !== '' && field.value !== undefined
  const isRequired = field.required === true
  const isCopied = copiedField === (field.name || field.label)

  return (
    <div className="flex items-center gap-2 py-1 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      {/* Copy button - compact */}
      <div className="flex-shrink-0">
        {hasValue ? (
          <button
            onClick={() => onCopy(field.value!)}
            className="p-1 text-gray-400 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title={isCopied ? 'Copiado!' : 'Copiar'}
          >
            {isCopied ? (
              <Icon name="check" size={16} className="text-green-500" />
            ) : (
              <Icon name="copy" size={16} />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}
      </div>

      {/* Label - fixed width */}
      <div className="flex-shrink-0 w-28">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {field.label}
        </span>
      </div>

      {/* Value - flex grow */}
      <div className="flex-1 min-w-0">
        {hasValue ? (
          <span className="text-sm text-gray-900 dark:text-white truncate block">
            {field.value}
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            —
          </span>
        )}
      </div>

      {/* Status indicator - compact */}
      <div className="flex-shrink-0">
        {hasValue ? (
          <Icon name="check-circle" size={16} className="text-green-500" />
        ) : isRequired ? (
          <Icon name="alert-circle" size={16} className="text-red-500" />
        ) : (
          <Icon
            name="minus-circle"
            size={16}
            className="text-gray-300 dark:text-gray-600"
          />
        )}
      </div>
    </div>
  )
}

export const GenericDataModal: React.FC<GenericDataModalProps> = ({
  data,
  isOpen,
  onClose,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const { copy } = useCopyToClipboard()

  const completeness = calculateCompleteness(data.fields)

  const handleCopyField = useCallback(
    (fieldName: string, value: string) => {
      copy(value)
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
    },
    [copy]
  )

  const handleCopyAll = useCallback(() => {
    const filledFields = data.fields
      .filter(f => f.value !== null && f.value !== '' && f.value !== undefined)
      .map(f => `${f.label}: ${f.value}`)
      .join('\n')

    copy(filledFields)
    setCopiedField('__all__')
    setTimeout(() => setCopiedField(null), 2000)
  }, [copy, data.fields])

  const handleActionClick = useCallback(() => {
    if (data.action?.type === 'copy_all') {
      handleCopyAll()
    } else if (data.action?.type === 'open_url' && data.action.url) {
      window.open(data.action.url, '_blank')
    }
  }, [data.action, handleCopyAll])

  if (!isOpen) return null

  const iconName =
    data.iconName ?? (data.type === 'helpdesk' ? 'ticket' : 'file-text')
  const legacyIcon = data.icon

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
        data-testid="data-modal-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-t-2xl lg:rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header - compact */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DynamicIcon
                  name={iconName}
                  fallbackValue={legacyIcon}
                  size={24}
                />
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {data.title}
                  </h3>
                  {data.subtitle && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {data.subtitle}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                aria-label="Fechar"
              >
                <Icon
                  name="x"
                  size={16}
                  className="text-gray-500 dark:text-gray-400"
                />
              </button>
            </div>

            {/* Completeness indicator - inline compact */}
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    completeness.percentage === 100
                      ? 'bg-green-500'
                      : completeness.requiredMissing > 0
                        ? 'bg-red-500'
                        : 'bg-yellow-500'
                  }`}
                  style={{ width: `${completeness.percentage}%` }}
                />
              </div>
              <span
                className={`text-xs font-medium ${
                  completeness.percentage === 100
                    ? 'text-green-600 dark:text-green-400'
                    : completeness.requiredMissing > 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-yellow-600 dark:text-yellow-400'
                }`}
              >
                {completeness.filled}/{completeness.total}
              </span>
            </div>
          </div>

          {/* Fields list - compact padding */}
          <div className="px-4 py-2 overflow-y-auto flex-1">
            {data.fields.map((field, index) => (
              <FieldItem
                key={field.name || `field-${index}`}
                field={field}
                onCopy={value =>
                  handleCopyField(field.name || field.label, value)
                }
                copiedField={copiedField}
              />
            ))}
          </div>

          {/* Actions - compact */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={handleActionClick}
                className="flex-1 px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                {copiedField === '__all__' ? (
                  <>
                    <Icon name="check" size={16} />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Icon name="copy" size={16} />
                    {data.action?.label || 'Copiar Tudo'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 1024px) {
          @keyframes scale-in {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </>
  )
}

export default GenericDataModal
